import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { IMAGES } from "../config";
import { useBookingModal } from "../context/BookingModalContext";

const Navigation = ({ isScrolled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { openBookingModal } = useBookingModal();

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "Weddings", path: "/weddings" },
    { name: "Private Hire", path: "/private-hire" },
    { name: "Festivals", path: "/festivals" },
    { name: "Menu", path: "/menu" },
    { name: "About", path: "/about" },
    { name: "Contact", path: "/contact" },
  ];

  const handleBookNow = () => {
    openBookingModal();
  };

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      role="navigation"
      aria-label="Main navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "glass shadow-lg" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to="/" className="flex items-center focus:outline-none focus:ring-2 focus:ring-[#a5dae0] focus:ring-offset-2 rounded-lg" data-testid="logo-link" aria-label="Bar Spot - Home">
            <img src={IMAGES.barspotLogo} alt="Bar Spot Logo" className="h-10 md:h-12 w-auto" />
          </Link>

          <div className="hidden lg:flex items-center gap-6 xl:gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                data-testid={`nav-${link.name.toLowerCase().replace(" ", "-")}`}
                aria-current={location.pathname === link.path ? "page" : undefined}
                className={`text-sm font-medium transition-colors hover:text-[#a5dae0] focus:outline-none focus:ring-2 focus:ring-[#a5dae0] focus:ring-offset-2 rounded px-2 py-1 ${
                  isScrolled ? "text-[#444444]" : "text-[#444444]"
                } ${location.pathname === link.path ? "text-[#a5dae0]" : ""}`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          <div className="hidden lg:flex items-center gap-4">
            <a
              href="tel:07554856712"
              data-testid="nav-phone"
              aria-label="Call us at 07554 856712"
              className="flex items-center gap-2 text-sm font-medium text-[#444444] hover:text-[#a5dae0] transition-colors focus:outline-none focus:ring-2 focus:ring-[#a5dae0] focus:ring-offset-2 rounded px-2 py-1"
            >
              <Phone size={16} aria-hidden="true" />
              <span>07554 856712</span>
            </a>
            <button
              onClick={handleBookNow}
              data-testid="nav-book-now"
              className="bg-[#a5dae0] text-[#1a1a1a] px-6 py-3 rounded-full font-semibold text-sm hover:bg-[#8bc9d0] transition-all hover:scale-105 active:scale-95 shadow-lg hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-[#a5dae0] focus:ring-offset-2"
            >
              Book Now
            </button>
          </div>

          <button
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-toggle"
            aria-expanded={isOpen}
            aria-controls="mobile-menu"
            aria-label={isOpen ? "Close menu" : "Open menu"}
            className="lg:hidden p-2 text-[#444444] focus:outline-none focus:ring-2 focus:ring-[#a5dae0] rounded-lg"
          >
            {isOpen ? <X size={24} aria-hidden="true" /> : <Menu size={24} aria-hidden="true" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            id="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden glass border-t border-white/20"
            role="menu"
          >
            <div className="px-4 md:px-6 py-4 space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  data-testid={`mobile-nav-${link.name.toLowerCase().replace(" ", "-")}`}
                  role="menuitem"
                  aria-current={location.pathname === link.path ? "page" : undefined}
                  className="block py-2 px-2 text-[#444444] font-medium hover:text-[#a5dae0] transition-colors focus:outline-none focus:ring-2 focus:ring-[#a5dae0] rounded"
                >
                  {link.name}
                </Link>
              ))}
              <button
                onClick={() => {
                  setIsOpen(false);
                  handleBookNow();
                }}
                data-testid="mobile-book-now"
                role="menuitem"
                className="w-full bg-[#a5dae0] text-[#1a1a1a] px-6 py-3 rounded-full font-semibold text-sm mt-4 focus:outline-none focus:ring-2 focus:ring-[#a5dae0] focus:ring-offset-2"
              >
                Book Now
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navigation;
